import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, UserCog } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ProfileModal from "@/components/app/ProfileModal";
import LogoTile from "@/components/app/LogoTile";

const initials = (name, email) => {
    const src = (name || email || "?").trim();
    if (!src) return "?";
    const parts = src.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return src.slice(0, 2).toUpperCase();
};

const AccountMenu = () => {
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const ref = useRef(null);
    const nav = useNavigate();

    useEffect(() => {
        const onClick = (e) => {
            if (open && ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        window.addEventListener("mousedown", onClick);
        window.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener("mousedown", onClick);
            window.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const doLogout = () => {
        setOpen(false);
        logout();
        nav("/login");
    };

    if (!user || user === false) return null;

    return (
        <>
            <div className="relative" ref={ref}>
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    className="flex items-center gap-2 px-2 py-1 rounded-full hover:bg-white/40"
                    aria-haspopup="menu"
                    aria-expanded={open}
                    data-testid="nav-account-button"
                >
                    <span
                        className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-800 text-[12px] font-semibold flex items-center justify-center"
                        aria-hidden
                    >
                        {initials(user.full_name, user.email)}
                    </span>
                    <span className="hidden sm:flex flex-col items-start leading-tight max-w-[180px]">
                        <span className="text-[13px] font-medium text-slate-900 truncate max-w-full">
                            {user.full_name || user.email}
                        </span>
                        {user.full_name && (
                            <span className="text-[11px] text-slate-500 truncate max-w-full">
                                {user.email}
                            </span>
                        )}
                    </span>
                    <ChevronDown
                        size={14}
                        strokeWidth={2}
                        className="text-slate-500 transition-transform"
                        style={{ transform: open ? "rotate(180deg)" : "none" }}
                    />
                </button>
                {open && (
                    <div
                        role="menu"
                        className="absolute right-0 mt-2 w-[240px] glass-card !p-0 overflow-hidden z-30"
                        data-testid="nav-account-menu"
                    >
                        <div className="px-4 py-3 border-b border-[var(--divider)]">
                            <div className="text-[13px] font-medium text-slate-900 truncate">
                                {user.full_name || user.email}
                            </div>
                            {user.full_name && (
                                <div className="text-[12px] text-slate-500 truncate">{user.email}</div>
                            )}
                            <div className="mt-1">
                                <span className={`chip ${user.role === "admin" ? "chip-certified" : "chip-neutral"}`}>
                                    {user.role}
                                </span>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => { setOpen(false); setProfileOpen(true); }}
                            className="w-full flex items-center gap-2 px-4 py-2 text-[13px] text-slate-800 hover:bg-slate-50 text-left"
                            data-testid="nav-open-profile"
                        >
                            <UserCog size={14} strokeWidth={2} />
                            Your profile
                        </button>
                        <button
                            type="button"
                            onClick={doLogout}
                            className="w-full flex items-center gap-2 px-4 py-2 text-[13px] text-red-600 hover:bg-red-50 text-left border-t border-[var(--divider)]"
                            data-testid="nav-logout"
                        >
                            <LogOut size={14} strokeWidth={2} />
                            Log out
                        </button>
                    </div>
                )}
            </div>
            <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
        </>
    );
};

export const AppShell = ({ children }) => {
    const { user } = useAuth();

    return (
        <div className="min-h-screen flex flex-col" data-testid="app-shell">
            <header className="glass-nav sticky top-0 z-20" data-testid="app-nav">
                <div className="max-w-[1120px] mx-auto px-6 sm:px-8 h-14 flex items-center justify-between">
                    <Link
                        to="/reports"
                        className="flex items-center gap-[10px] font-semibold text-[15px] text-slate-900"
                        data-testid="nav-wordmark"
                    >
                        <LogoTile size={32} testId="nav-logo" />
                        ImpressSite Pro
                    </Link>
                    <div className="flex items-center gap-3">
                        {user && user !== false ? (
                            <>
                                {user.role === "admin" && (
                                    <Link
                                        to="/admin/users"
                                        className="text-[13px] font-medium text-emerald-700 hover:text-emerald-800"
                                        data-testid="nav-admin-link"
                                    >
                                        Admin
                                    </Link>
                                )}
                                <AccountMenu />
                            </>
                        ) : null}
                    </div>
                </div>
            </header>
            <main className="flex-1 w-full">
                <div className="max-w-[1120px] mx-auto px-6 sm:px-8 py-8 route-fade">
                    {children}
                </div>
            </main>
            <footer className="mt-8">
                <div className="max-w-[1120px] mx-auto px-6 sm:px-8 h-12 flex items-center justify-between text-[12px] text-slate-500">
                    <span>ImpressSite Pro</span>
                    <span>Built for the field.</span>
                </div>
            </footer>
        </div>
    );
};

export default AppShell;
